import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";

import PublicLayout from "./components/site/PublicLayout";
import Home from "./pages/Home";
import ExamPage from "./pages/ExamPage";
import ProductPage from "./pages/ProductPage";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Pricing from "./pages/Pricing";
import FreeTests from "./pages/FreeTests";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import SearchPage from "./pages/SearchPage";
import LegalPage from "./pages/LegalPage";
import NotFound from "./pages/NotFound";

import AuthPage from "./pages/auth/AuthPage";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import Checkout from "./pages/Checkout";

import StudentLayout from "./pages/student/StudentLayout";
import Dashboard from "./pages/student/Dashboard";
import MyTestSeries from "./pages/student/MyTestSeries";
import SeriesTests from "./pages/student/SeriesTests";
import Bookmarks from "./pages/student/Bookmarks";
import WrongQuestions from "./pages/student/WrongQuestions";
import Performance from "./pages/student/Performance";
import Orders from "./pages/student/Orders";
import Notifications from "./pages/student/Notifications";
import Profile from "./pages/student/Profile";

import TestInstructions from "./pages/exam/TestInstructions";
import TestEngine from "./pages/exam/TestEngine";
import Result from "./pages/exam/Result";

import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import { AdminExams, AdminProducts, AdminCoupons, AdminTestimonials, AdminFaqs, AdminBlog, AdminPages } from "./pages/admin/AdminResources";
import CustomPage from "./pages/CustomPage";
import AdminTests from "./pages/admin/AdminTests";
import AdminStudents from "./pages/admin/AdminStudents";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminSystem from "./pages/admin/AdminSystem";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-ink">
    <div className="w-10 h-10 rounded-full border-2 border-ember border-t-transparent animate-spin" />
  </div>
);

function Protected({ children, admin }) {
  const { user, ready } = useAuth();
  if (!ready) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role === "student") return <Navigate to="/student/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      {/* Auth */}
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/register" element={<AuthPage mode="register" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Exam engine (fullscreen) */}
      <Route path="/test/:testId/instructions" element={<Protected><TestInstructions /></Protected>} />
      <Route path="/test/:testId/attempt" element={<Protected><TestEngine /></Protected>} />
      <Route path="/result/:attemptId" element={<Protected><Result /></Protected>} />

      {/* Checkout */}
      <Route path="/checkout/:slug" element={<Protected><Checkout /></Protected>} />

      {/* Student */}
      <Route path="/student" element={<Protected><StudentLayout /></Protected>}>
        <Route index element={<Navigate to="/student/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="test-series" element={<MyTestSeries />} />
        <Route path="series/:productId" element={<SeriesTests />} />
        <Route path="bookmarks" element={<Bookmarks />} />
        <Route path="wrong-questions" element={<WrongQuestions />} />
        <Route path="performance" element={<Performance />} />
        <Route path="orders" element={<Orders />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="profile" element={<Profile />} />
      </Route>

      {/* Admin */}
      <Route path="/admin" element={<Protected admin><AdminLayout /></Protected>}>
        <Route index element={<AdminDashboard />} />
        <Route path="exams" element={<AdminExams />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="tests" element={<AdminTests />} />
        <Route path="students" element={<AdminStudents />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="coupons" element={<AdminCoupons />} />
        <Route path="blog" element={<AdminBlog />} />
        <Route path="pages" element={<AdminPages />} />
        <Route path="testimonials" element={<AdminTestimonials />} />
        <Route path="faqs" element={<AdminFaqs />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="system" element={<AdminSystem />} />
      </Route>

      {/* Public */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/free-tests" element={<FreeTests />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/p/:slug" element={<CustomPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/product/:slug" element={<ProductPage />} />
        <Route path="/privacy-policy" element={<LegalPage slug="privacy-policy" />} />
        <Route path="/terms-and-conditions" element={<LegalPage slug="terms-and-conditions" />} />
        <Route path="/refund-policy" element={<LegalPage slug="refund-policy" />} />
        <Route path="/disclaimer" element={<LegalPage slug="disclaimer" />} />
        <Route path="/cookie-policy" element={<LegalPage slug="cookie-policy" />} />
        <Route path="/:examSlug" element={<ExamPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
